import { useCallback } from 'react';
import type {
  EnvironmentKey,
  ConfigEntityListQueryResult,
} from '@web-config-app/core';
import { useConfigEntityListQuery } from '../use-config-entity-list-query/use-config-entity-list-query';
import type { FindConfigEntityByDomainEntityId } from '../../types/operation.types';

export interface UseFindConfigEntityByDomainIdProps {
  /**
   * The environment to search for the config entity
   */
  environment: EnvironmentKey;
}

/**
 * Hook that provides a function to find a config entity by its domainEntityId and domainEntityType.
 *
 * This is useful for resolving linked entity references which store the domainEntityId
 * as a string in their attributes, rather than using relationships.
 *
 * @param props.environment - The environment to search for the config entity
 * @returns An async function that takes domainEntityId and domainEntityType and returns the config entity
 *
 * @example
 * ```typescript
 * const findEntity = useFindConfigEntityByDomainId({ environment: 'staging' });
 * const result = await findEntity('config-entity-123', 'config-domain-entity');
 * if (result.status === 'success' && result.data) {
 *   console.log('Found entity:', result.data);
 * }
 * ```
 */
export const useFindConfigEntityByDomainId = ({
  environment,
}: UseFindConfigEntityByDomainIdProps): FindConfigEntityByDomainEntityId => {
  const configEntityListQuery = useConfigEntityListQuery({ environment });

  return useCallback(
    async (
      domainEntityId: string,
      domainEntityType?: string,
    ): Promise<ConfigEntityListQueryResult> => {
      try {
        const results = await configEntityListQuery({
          'filter[domainEntityID]': domainEntityId,
          'page[offset]': '0',
          'page[size]': '1',
          ...(domainEntityType
            ? { 'filter[domainEntityType]': domainEntityType }
            : {}),
        });

        const entity = results[0];

        if (!entity) {
          return {
            status: 'error',
            error: new Error('No entity found'),
          };
        }

        return { data: entity, status: 'success' };
      } catch (error) {
        return { status: 'error', error };
      }
    },
    [configEntityListQuery],
  );
};
